import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Trash2, PenLine } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 500 * 1024;

export default function SignatureDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [preview, setPreview] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPreview(user?.signature_data_url || "");
      setDirty(false);
    }
  }, [open, user]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      toast.error("Please upload a PNG or JPEG image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File is too large. Please keep signature images under 500KB (crop to just the signature).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(String(reader.result));
      setDirty(true);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!preview || !preview.startsWith("data:image/")) {
      toast.error("No signature selected");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/profile/signature", { data_url: preview });
      toast.success("Signature saved. It will appear on your POs and GRNs going forward.");
      await refresh?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your saved signature? Your name will still appear on PDFs, without the image.")) return;
    setSaving(true);
    try {
      await api.delete("/auth/profile/signature");
      setPreview("");
      setDirty(false);
      toast.success("Signature removed");
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[#7C1D3A]" /> My Signature
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600 leading-relaxed">
            Upload a scanned or transparent-background image of your signature (PNG or JPEG, under 500KB).
            It flows onto every PO PDF you prepare/approve and every GRN you sign for.
          </p>

          <div className="border border-dashed border-slate-300 rounded-md p-6 bg-slate-50 flex items-center justify-center min-h-[140px]">
            {preview ? (
              <img
                src={preview}
                alt="Signature preview"
                className="max-h-24 max-w-full object-contain"
                data-testid="signature-preview"
              />
            ) : (
              <div className="text-slate-400 text-sm text-center">No signature uploaded yet</div>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={onFile}
            data-testid="signature-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={saving}
              data-testid="signature-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {preview ? "Replace image" : "Upload image"}
            </Button>
            {user?.signature_data_url && (
              <Button
                variant="outline"
                onClick={remove}
                disabled={saving}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="signature-remove-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Remove
              </Button>
            )}
          </div>

          <div className="text-[11px] text-slate-500 leading-snug">
            Tip: for the crispest render on PDFs, use a PNG with a transparent background (roughly 3:1 aspect ratio, e.g. 900×300 px).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            disabled={saving || !dirty || !preview}
            onClick={save}
            data-testid="signature-save-btn"
          >
            {saving ? "Saving..." : "Save signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
